import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/app/store'
import type {
  ApplicationState,
  ResidentApplicationData,
  UpdateApplicationRequest,
} from '@/types/application'
import * as applicationService from '@/lib/api/applicationService'
import { removeStorageItem, setStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import { isApplicationReadOnly } from '@/types/application'

/**
 * Track pending fetch requests by applicant ID to prevent duplicates
 */
const pendingFetchRequests = new Map<string | number, Promise<ResidentApplicationData>>()

/**
 * Initial application state
 */
const initialState: ApplicationState = {
  applicationData: null,
  applications: [],
  isLoading: false,
  error: null,
  isReadOnly: false,
  hasMultipleActiveApplications: false,
  hasSelectedApplication: false,
}

/**
 * Async thunk for fetching all applications
 */
export const fetchApplications = createAsyncThunk<
  ResidentApplicationData[],
  { sortBy?: string; sortOrder?: string } | void,
  { rejectValue: string }
>('application/fetchAll', async (params, { rejectWithValue }) => {
  try {
    let sortBy: string | undefined
    let sortOrder: string | undefined
    
    if (params && typeof params === 'object') {
      sortBy = params.sortBy
      sortOrder = params.sortOrder
    }
    
    const applications = await applicationService.getResidentApplications(sortBy, sortOrder)
    return applications
  } catch (error) {
    const apiError = error as { message: string; isAuthError?: boolean }
    return rejectWithValue(apiError.message)
  }
})

/**
 * Async thunk for fetching application by ID
 * Includes deduplication to prevent multiple concurrent requests for the same ID
 */
export const fetchApplicationById = createAsyncThunk<
  ResidentApplicationData,
  string | number,
  { rejectValue: string }
>('application/fetchById', async (applicantId, { rejectWithValue }) => {
  // Normalize applicantId to string for consistent Map key
  const idKey = String(applicantId)
  
  // Check if there's already a pending request for this ID
  const pendingRequest = pendingFetchRequests.get(idKey)
  if (pendingRequest) {
    // Return the existing promise instead of making a new request
    // If it fails, it will be handled by the outer try-catch
    return await pendingRequest
  }
  
  // Create new request and store it
  const requestPromise = applicationService.getResidentApplicationById(applicantId)
    .finally(() => {
      // Remove from pending requests when done (success or failure)
      pendingFetchRequests.delete(idKey)
    })
  
  pendingFetchRequests.set(idKey, requestPromise)
  
  try {
    return await requestPromise
  } catch (error) {
    const apiError = error as { message: string; isAuthError?: boolean }
    return rejectWithValue(apiError.message || 'Failed to fetch application')
  }
})

/**
 * Async thunk for updating application
 */
export const updateApplication = createAsyncThunk<
  ResidentApplicationData,
  { applicantId: string | number; updateData: UpdateApplicationRequest },
  { rejectValue: string }
>('application/update', async ({ applicantId, updateData }, { rejectWithValue }) => {
  try {
    // Update the application
    await applicationService.updateResidentApplication(
      applicantId,
      updateData
    )
    
    // Always fetch the latest complete data after update to ensure Redux has all fields
    // This ensures deleted items and all updates are reflected correctly
    const latestData = await applicationService.getResidentApplicationById(applicantId)
    return latestData
  } catch (error) {
    const apiError = error as { message: string; isAuthError?: boolean }
    return rejectWithValue(apiError.message)
  }
})

/**
 * Bank-related field names. When merging after bank details upload, only these
 * fields are updated in Redux so employment and other unsaved form data are preserved.
 */
const BANK_DETAILS_FIELDS: (keyof ResidentApplicationData)[] = [
  'bank_name',
  'ninja_income_proof',
  'plaid_opt_out',
]

/**
 * Merge only bank-related fields from API response into existing application data.
 * Used after bank details popup submit so employment and other form data are not overwritten.
 */
function mergeBankDetailsOnly(
  existing: ResidentApplicationData | null,
  incoming: ResidentApplicationData
): ResidentApplicationData {
  if (!existing) {
    return incoming
  }
  const merged = { ...existing }
  for (const key of BANK_DETAILS_FIELDS) {
    if (key in incoming) {
      ;(merged as Record<string, unknown>)[key] = (incoming as Record<string, unknown>)[key]
    }
  }
  return merged
}

function numericId(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function idsReferToSameApplication(
  a: ResidentApplicationData | null | undefined,
  b: ResidentApplicationData | null | undefined,
): boolean {
  if (!a || !b) return false

  const aInvite = numericId(a.invite_id)
  const bInvite = numericId(b.invite_id)
  if (aInvite !== null && bInvite !== null) {
    return aInvite === bInvite
  }

  const aAppId = numericId(a.fk_application_id ?? a.application_id)
  const bAppId = numericId(b.fk_application_id ?? b.application_id)
  if (aAppId !== null && bAppId !== null) {
    return aAppId === bAppId
  }

  const aApplicant = numericId(a.applicant_id)
  const bApplicant = numericId(b.applicant_id)
  if (aApplicant !== null && bApplicant !== null) {
    return aApplicant === bApplicant
  }

  return false
}

function withResolvedApplicationIds(data: ResidentApplicationData): ResidentApplicationData {
  const nestedProperty = data.property
  const propertyId = data.property_id ?? data.fk_property_id ?? nestedProperty?.property_id
  const inviteId = data.invite_id ?? (data as { fk_invite_id?: number }).fk_invite_id
  const applicationId = data.application_id ?? data.fk_application_id
  const propertyName = data.property_name ?? nestedProperty?.property_name

  return {
    ...data,
    ...(propertyId !== undefined && propertyId !== null ? { property_id: propertyId } : {}),
    ...(inviteId !== undefined && inviteId !== null ? { invite_id: inviteId } : {}),
    ...(applicationId !== undefined && applicationId !== null ? { application_id: applicationId } : {}),
    ...(propertyName ? { property_name: propertyName } : {}),
  }
}

/**
 * Helper function to preserve critical ID fields when merging application data
 * This ensures that IDs from existing state are preserved if they're missing in incoming data
 */
function preserveApplicationIds(
  existing: ResidentApplicationData | null,
  incoming: ResidentApplicationData
): ResidentApplicationData {
  const resolvedIncoming = withResolvedApplicationIds(incoming)
  if (!existing) {
    return resolvedIncoming
  }

  // Never copy property/invite IDs from a different dashboard row onto the selected application.
  if (!idsReferToSameApplication(existing, resolvedIncoming)) {
    return resolvedIncoming
  }

  // Check if application_id exists in incoming data (not just if it's truthy)
  // This handles the case where fetchApplications returns items without application_id
  const hasApplicationId = incoming.application_id !== undefined && incoming.application_id !== null && incoming.application_id !== ''

  return withResolvedApplicationIds({
    ...incoming,
    application_id: hasApplicationId ? incoming.application_id : existing.application_id,
    applicant_id: incoming.applicant_id ?? existing.applicant_id,
    user_id: incoming.user_id ?? existing.user_id,
    property_id: resolvedIncoming.property_id ?? existing.property_id,
    invite_id: resolvedIncoming.invite_id ?? existing.invite_id,
  })
}

/**
 * Helper function to store applicant_id in localStorage if present
 */
function storeApplicantId(applicantId: number | string | undefined): void {
  if (applicantId) {
    setStorageItem(STORAGE_KEYS.applicantId, String(applicantId))
  }
}

/**
 * Application slice
 */
const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    /**
     * Clear application error
     */
    clearError: (state) => {
      state.error = null
    },

    /**
     * Set application data
     */
    setApplicationData: (state, action: PayloadAction<ResidentApplicationData>) => {
      state.applicationData = action.payload
      // Check if read-only based on allow_edit flag, status, or paid_amount
      state.isReadOnly = isApplicationReadOnly(action.payload)
    },

    /**
     * Clear application data
     */
    clearApplicationData: (state) => {
      state.applicationData = null
      state.applications = []
      state.isReadOnly = false
      state.error = null
      // Clear stored applicant_id
      removeStorageItem(STORAGE_KEYS.applicantId)
    },

    /**
     * Set read-only mode
     */
    setReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isReadOnly = action.payload
    },

    /**
     * Set multiple active applications flag
     * When true, user must select an application from dashboard before accessing application pages
     */
    setHasMultipleActiveApplications: (state, action: PayloadAction<boolean>) => {
      state.hasMultipleActiveApplications = action.payload
    },

    /**
     * Set selected application flag
     * When true, user has explicitly selected an application from the dashboard
     */
    setHasSelectedApplication: (state, action: PayloadAction<boolean>) => {
      state.hasSelectedApplication = action.payload
    },

    /**
     * Merge only bank-related fields from an API response into current application data.
     * Used after bank details popup submit so employment and other unsaved form data are preserved.
     */
    mergeBankDetailsFromResponse: (state, action: PayloadAction<ResidentApplicationData>) => {
      if (!state.applicationData) {
        state.applicationData = action.payload
        return
      }
      state.applicationData = mergeBankDetailsOnly(state.applicationData, action.payload)
    },
  },
  extraReducers: (builder) => {
    // Fetch all applications
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.isLoading = false
        state.applications = action.payload
        if (action.payload.length === 0) {
          return
        }

        const current = state.applicationData
        const matched = current
          ? action.payload.find((app) => idsReferToSameApplication(current, app))
          : undefined
        // If the current invite is still in the list, keep it. Otherwise keep
        // the previous default (list[0]) so login/single-app/dashboard load is unchanged.
        const nextApp = matched && current ? current : action.payload[0]
        const existingForMerge = matched && current ? matched : current
        const mergedData = preserveApplicationIds(existingForMerge, nextApp)
        state.applicationData = mergedData
        state.isReadOnly = isApplicationReadOnly(mergedData)
        storeApplicantId(state.applicationData.applicant_id)
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch applications'
      })

    // Fetch application by ID
    builder
      .addCase(fetchApplicationById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchApplicationById.fulfilled, (state, action) => {
        state.isLoading = false
        // fetchApplicationById returns complete data, but preserve IDs in edge cases
        const mergedData = preserveApplicationIds(state.applicationData, action.payload)
        state.applicationData = mergedData
        // Check if read-only based on allow_edit flag, status, or application fee paid
        // Always recalculate to ensure it's up-to-date with latest status
        state.isReadOnly = isApplicationReadOnly(mergedData)
        // Store applicant_id for persistence
        storeApplicantId(state.applicationData.applicant_id)
      })
      .addCase(fetchApplicationById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch application'
      })

    // Update application
    // Note: updateApplication does NOT set state.isLoading or state.error.
    // - isLoading is intentionally not set because the parent ResidentApplication
    //   component shows a full-page loader when isLoading && !hasApplicationData,
    //   which would flash during save operations. Tab components manage their own
    //   loading states (e.g. isSaving).
    // - error is intentionally not set because the parent ResidentApplication
    //   component redirects to /applicant/dashboard on any error. Update errors
    //   (e.g. "Signature name must match...") should be handled locally by the
    //   tab components via .unwrap().catch(), not cause a redirect.
    builder
      .addCase(updateApplication.pending, (_state) => {
        // No state changes - tab components manage their own loading/saving states
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.isLoading = false
        // Merge updated data with existing data, preserving critical IDs
        const mergedData = preserveApplicationIds(state.applicationData, action.payload)
        state.applicationData = mergedData
        // Recalculate read-only state after update (status or allow_edit may have changed)
        state.isReadOnly = isApplicationReadOnly(mergedData)
      })
      .addCase(updateApplication.rejected, (state) => {
        state.isLoading = false
        // Do NOT set state.error here - update errors are handled locally by
        // the calling tab component via .unwrap().catch(). Setting state.error
        // would trigger the parent ResidentApplication's useEffect to redirect
        // to /applicant/dashboard, which is only appropriate for fetch errors.
      })
  },
})

// Export actions
export const {
  clearError,
  setApplicationData,
  clearApplicationData,
  setReadOnly,
  setHasMultipleActiveApplications,
  setHasSelectedApplication,
  mergeBankDetailsFromResponse,
} = applicationSlice.actions

// Export selectors
export const selectApplication = (state: RootState) => state.application
export const selectApplicationData = (state: RootState) => state.application.applicationData
export const selectApplications = (state: RootState) => state.application.applications
export const selectApplicationLoading = (state: RootState) => state.application.isLoading
export const selectApplicationError = (state: RootState) => state.application.error
export const selectIsReadOnly = (state: RootState) => state.application.isReadOnly
export const selectHasMultipleActiveApplications = (state: RootState) => state.application.hasMultipleActiveApplications
export const selectHasSelectedApplication = (state: RootState) => state.application.hasSelectedApplication

// Export reducer
export default applicationSlice.reducer

 